import React, { useCallback } from "react";
import {
  LibraryItemTransform,
  useLibraryItemDataTransform,
} from "./useLibraryItemDataTransform";
import { ChatAttachmentItem } from "@src/features/aiAssist/uiComponents/ChatAttachment";
import { ChatAttachmentLibraryItemFragment } from "@src/features/assistantChat/aiChat.generated";

export interface UseAiChatComposerLibraryItemTransformProps {
  onAttachmentsChanged?: React.Dispatch<
    React.SetStateAction<ChatAttachmentItem[]>
  >;
}

export const useAiChatComposerLibraryItemTransform = ({
  onAttachmentsChanged,
}: UseAiChatComposerLibraryItemTransformProps): {
  transformLibraryItem: (
    libraryItem: ChatAttachmentLibraryItemFragment,
  ) => void;
} => {
  const onTransformStarted = useCallback(
    (libraryItemTransform: LibraryItemTransform) => {
      if (!onAttachmentsChanged) return;
      const newAttachment = { transformingLibraryItem: libraryItemTransform };
      onAttachmentsChanged((prev) => [newAttachment, ...(prev ?? [])]);
    },
    [onAttachmentsChanged],
  );

  const onTransformCompleted = useCallback(
    (libraryItemTransform: LibraryItemTransform) => {
      if (!onAttachmentsChanged) return;
      onAttachmentsChanged((prev) =>
        prev.map((att) => {
          if (att.transformingLibraryItem?.uuid === libraryItemTransform.uuid) {
            return {
              ...att,
              transformingLibraryItem: libraryItemTransform,
              libraryItemFragment: libraryItemTransform.libraryItemFragment,
            };
          }
          return att;
        }),
      );
    },
    [onAttachmentsChanged],
  );

  const onTransformError = useCallback(
    (libraryItemTransform: LibraryItemTransform) => {
      if (!onAttachmentsChanged) return;
      onAttachmentsChanged((prev) =>
        prev.map((att) => {
          if (att.transformingLibraryItem?.uuid === libraryItemTransform.uuid) {
            return {
              ...att,
              transformingLibraryItem: libraryItemTransform,
            };
          }
          return att;
        }),
      );
    },
    [onAttachmentsChanged],
  );

  const { transformLibraryItem } = useLibraryItemDataTransform({
    onTransformStarted,
    onTransformCompleted,
    onTransformError,
  });

  return {
    transformLibraryItem,
  };
};
